/**
 * VistoeraTASK-00638 — Member grant draft helpers (INHERIT/ALLOW/DENY).
 * Isolated from plan + workspace drafts: never share state across members.
 */
import { grantKey, parseGrantKey, type GrantKey, type MatrixRow } from './authMatrixDraft'
import type { WorkspaceDecision } from './workspaceAuthMatrixDraft'
import { normalizeWorkspaceDecision } from './workspaceAuthMatrixDraft'

export type MemberDecision = WorkspaceDecision

export type MemberDraftMaps = {
  baseline: Record<string, MemberDecision>
  draft: Record<string, MemberDecision>
  workspaceEffective: Record<string, boolean>
  memberEffective: Record<string, boolean>
  nodeIds: string[]
  channelIds: string[]
}

export function mapsFromMemberRows(rows: MatrixRow[]): MemberDraftMaps {
  const baseline: Record<string, MemberDecision> = {}
  const draft: Record<string, MemberDecision> = {}
  const workspaceEffective: Record<string, boolean> = {}
  const memberEffective: Record<string, boolean> = {}
  const nodeIds: string[] = []
  const channelIds: string[] = []
  const seenChannels = new Set<string>()

  for (const row of rows) {
    const nid = row.node.subject_id
    nodeIds.push(nid)
    const nk = grantKey('node', nid)
    const nd = normalizeWorkspaceDecision(row.node.decision)
    baseline[nk] = nd
    draft[nk] = nd
    workspaceEffective[nk] = Boolean(
      (row.node as { workspace_effective_allowed?: boolean }).workspace_effective_allowed ??
        row.node.effective_allowed,
    )
    memberEffective[nk] = Boolean(row.node.effective_allowed)

    for (const ch of row.channels || []) {
      const ck = grantKey('channel', ch.subject_id)
      const cd = normalizeWorkspaceDecision(ch.decision)
      baseline[ck] = cd
      draft[ck] = cd
      workspaceEffective[ck] = Boolean(
        (ch as { workspace_effective_allowed?: boolean }).workspace_effective_allowed ?? ch.effective_allowed,
      )
      memberEffective[ck] = Boolean(ch.effective_allowed)
      if (!seenChannels.has(ch.subject_id)) {
        seenChannels.add(ch.subject_id)
        channelIds.push(ch.subject_id)
      }
    }
  }

  return { baseline, draft, workspaceEffective, memberEffective, nodeIds, channelIds }
}

export function isMemberDirty(
  baseline: Record<string, MemberDecision>,
  draft: Record<string, MemberDecision>,
): boolean {
  for (const [key, value] of Object.entries(draft)) {
    if ((baseline[key] ?? 'INHERIT') !== value) return true
  }
  for (const key of Object.keys(baseline)) {
    if (!(key in draft) && baseline[key] !== 'INHERIT') return true
  }
  return false
}

export function memberDirtyEntries(
  baseline: Record<string, MemberDecision>,
  draft: Record<string, MemberDecision>,
): Array<{ kind: 'node' | 'channel'; subjectId: string; decision: MemberDecision }> {
  const out: Array<{ kind: 'node' | 'channel'; subjectId: string; decision: MemberDecision }> = []
  const keys = new Set([...Object.keys(baseline), ...Object.keys(draft)])
  for (const key of keys) {
    const next = draft[key] ?? 'INHERIT'
    const prev = baseline[key] ?? 'INHERIT'
    if (next === prev) continue
    const { kind, subjectId } = parseGrantKey(key as GrantKey)
    out.push({ kind, subjectId, decision: next })
  }
  return out
}

export function countMemberOverrides(
  draft: Record<string, MemberDecision>,
  nodeIds: string[],
  channelIds: string[],
): { nodes: number; channels: number } {
  let nodes = 0
  let channels = 0
  for (const id of nodeIds) {
    if (draft[grantKey('node', id)] !== 'INHERIT') nodes += 1
  }
  for (const id of channelIds) {
    if (draft[grantKey('channel', id)] !== 'INHERIT') channels += 1
  }
  return { nodes, channels }
}

export function draftAllMemberInherit(
  nodeIds: string[],
  channelIds: string[],
): Record<string, MemberDecision> {
  const draft: Record<string, MemberDecision> = {}
  for (const id of nodeIds) draft[grantKey('node', id)] = 'INHERIT'
  for (const id of channelIds) draft[grantKey('channel', id)] = 'INHERIT'
  return draft
}

/** Copy only explicit member decisions from a source matrix into a draft keyed for the target. */
export function draftFromSourceMemberRows(sourceRows: MatrixRow[]): Record<string, MemberDecision> {
  const draft: Record<string, MemberDecision> = {}
  for (const row of sourceRows) {
    draft[grantKey('node', row.node.subject_id)] = normalizeWorkspaceDecision(row.node.decision)
    for (const ch of row.channels || []) {
      draft[grantKey('channel', ch.subject_id)] = normalizeWorkspaceDecision(ch.decision)
    }
  }
  return draft
}

export function memberDecisionLabel(d: MemberDecision): string {
  if (d === 'ALLOW') return '允许'
  if (d === 'DENY') return '禁止'
  return '继承团队'
}

export function workspaceAllowedLabel(allowed: boolean | undefined): string {
  if (allowed === true) return '允许'
  if (allowed === false) return '禁止'
  return '—'
}
